/**
 * useArSignifyInsert Hook
 *
 * Provides the insertion logic for adding a row from a Signify-created LineItem record.
 * Fetches the entity record, applies elementMapping, and calls actions.addRow.
 *
 * `options.afterRowId` is the row the new row is inserted below, resolved by the caller from the
 * table's selection at the moment it asks — after the modal's own awaited write, with the modal
 * still over the table. Without it every API-sourced part appended at the bottom of the table
 * while a typed row landed under the ticked row (SYD-2373).
 *
 * `options.quantity` is the operator's own quantity, written to the row's `quantity` cell after the
 * elementMapping loop so it wins over any mapped value. It goes to the row and never to the Products
 * record: a record is a catalogue entry upserted by product id, so the next BOM inserting the same
 * part would overwrite it. A table with no `quantity` column simply has nowhere to put it.
 *
 * Both insert functions resolve to the new row's id, or to null on every path that wrote nothing.
 * The multi-part fetch stamps a part as inserted only after a row exists, and null is how it learns
 * the row does not, so the stamp is skipped and the next press retries the part.
 *
 * `findRowsHoldingLineItems` asks the table — not the loaded rows — which of a set of parts it
 * already holds. Only the current page is loaded, so a scan of `rowsState.docs` would miss rows on
 * every other page and duplicate exactly the parts it was meant to protect. Identities are deduped
 * and then chunked by thirty, Firestore's `in` limit, and every field filtered on is a map
 * subfield, which Firestore indexes automatically; the whole conjunction is equalities, which
 * Firestore serves by merging those single-field indexes.
 *
 * It carries the table's split, under exactly the condition the two insert paths write the split
 * cell under. A split is a location, not a view: the operator who fetches a part on "Floor 2" has
 * asked for it there, and a row of the same part on "Floor 1" is a different line of the bill of
 * materials. Without the constraint that fetch is stamped inserted and no row ever appears.
 * The constraint matches the split cell the way this file WRITES it — a bare string. A row whose
 * split cell holds a reference object instead, written by some other path, is not recognised and
 * its part is inserted again; that is a visible extra row rather than a part that silently never
 * arrives, which is the failure worth having.
 */

import { useCallback, useContext } from 'react';
import { getDocs, query, where } from 'firebase/firestore';
import { UIElementType, type EntityRecord, type Fields } from '#types/System.types';
import type {
  ArCell,
  ArCellValue,
  ArColumnDefinition,
  ArColumnReference,
  ArEntityReference,
  ArManualEntryValue,
  ArRowId,
  TypeId,
} from '#types/System.Artifacts.types';
import { SIGNIFY_PRODUCTS_ENTITY_ID } from '#types/System.Parameters.types';
import { MessageContext } from '@/providers/MessageContext';
import { useGet } from '@/use/data/useGet';
import { useStoreNonReactive } from '@/store';
import type {
  SignifyFetchJobExistingRows,
  SignifyFetchJobRowIdentities,
} from '../SignifyProductButton/SignifyFetchJobModal/index.types';
import { ArTableActionsContext } from '../../../components/ArTable/context';
import { useArTableSplit } from '../../../components/ArTable/split';

type LineItemsColumn = ArColumnReference & { referenceType: 'entity'; entityId: string };

type UseArSignifyInsertParams = {
  columns: readonly ArColumnDefinition[];
  lineItemsColumn: LineItemsColumn | undefined;
  protectedTypeIds: ReadonlySet<TypeId>;
  disabled?: boolean;
};

type ArSignifyInsertOptions = {
  quantity?: number;
  afterRowId?: ArRowId;
};

type UseArSignifyInsertResult = {
  insertRowFromLineItem: (entityRecordId: string, options?: ArSignifyInsertOptions) => Promise<string | null>;
  insertManualRow: (
    recordName: string,
    fields: Fields,
    entityReferences: EntityRecord['entityReferences'],
    options?: ArSignifyInsertOptions,
  ) => Promise<string | null>;
  findRowsHoldingLineItems: (
    identities: SignifyFetchJobRowIdentities,
  ) => Promise<SignifyFetchJobExistingRows>;
};

/** Firestore's ceiling on the values of one `in` filter. */
const IN_FILTER_LIMIT = 30;

/** The conventional ArTable row quantity column, matching the optimizer and kit scaling defaults. */
export const QUANTITY_TYPE_ID = 'quantity';

/**
 * Hook for inserting an ArTable row from a Signify-created LineItems entity record.
 * Applies the LineItems column's elementMapping to populate cells.
 * Returns a no-op if lineItemsColumn is undefined.
 */
function useArSignifyInsert({
  columns,
  lineItemsColumn,
  protectedTypeIds,
  disabled = false,
}: UseArSignifyInsertParams): UseArSignifyInsertResult {
  const actions = useContext(ArTableActionsContext);
  const { messageApi } = useContext(MessageContext);
  const { get } = useGet();
  const split = useArTableSplit(columns);
  const { persist: rowsPersist } = useStoreNonReactive('rows');

  const writeQuantityCell = useCallback((
    initialCells: Record<TypeId, ArCell>,
    options?: ArSignifyInsertOptions,
  ): void => {
    if (options?.quantity === undefined) return;
    const quantityColumn = columns.find((c) => c.typeId === QUANTITY_TYPE_ID);
    if (!quantityColumn || protectedTypeIds.has(quantityColumn.typeId)) return;
    initialCells[quantityColumn.typeId] = { value: options.quantity };
  }, [columns, protectedTypeIds]);

  const insertRowFromLineItem = useCallback(async (
    entityRecordId: string,
    options?: ArSignifyInsertOptions,
  ): Promise<string | null> => {
    if (disabled || !actions || !lineItemsColumn) return null;

    if (protectedTypeIds.has(lineItemsColumn.typeId)) {
      messageApi.error('Unable to insert: Line Items column is protected.');
      return null;
    }

    const entityPath = `entities/${SIGNIFY_PRODUCTS_ENTITY_ID}/records`;

    let record: EntityRecord | null;
    try {
      record = await get<EntityRecord>(entityPath, entityRecordId);
    } catch {
      messageApi.error('Failed to fetch Line Item record.');
      return null;
    }

    if (!record) {
      messageApi.error('Line Item record not found.');
      return null;
    }

    const recordName = record.name || 'Line Item';

    const initialCells: Record<TypeId, ArCell> = {};

    const lineItemRef: ArEntityReference = {
      referenceType: 'entity',
      docId: entityRecordId,
      cacheLabel: recordName,
    };
    if (lineItemsColumn.entityGroupId) {
      lineItemRef.entityGroupId = lineItemsColumn.entityGroupId;
    }

    initialCells[lineItemsColumn.typeId] = { value: lineItemRef };

    for (const mapping of lineItemsColumn.elementMapping ?? []) {
      if (mapping.target.type !== 'column') continue;

      const targetTypeId = mapping.target.id as TypeId;
      if (!targetTypeId) continue;
      if (targetTypeId === lineItemsColumn.typeId) continue;
      if (protectedTypeIds.has(targetTypeId)) continue;

      const targetColumn = columns.find((c) => c.typeId === targetTypeId);
      if (!targetColumn) continue;

      const rawValue = (() => {
        if (mapping.source.type === 'field') {
          return record.fields?.[mapping.source.id]?.value ?? null;
        }
        const ref = record.entityReferences?.[mapping.source.id];
        return ref?.entityRecordId ?? null;
      })();

      const value: ArCellValue = (() => {
        if (
          targetColumn.uiElementType === UIElementType.Reference &&
          'referenceType' in targetColumn &&
          targetColumn.referenceType === 'entity' &&
          typeof rawValue === 'string' &&
          rawValue.length > 0
        ) {
          const ref = record.entityReferences?.[mapping.source.id];
          const entityRef: ArEntityReference = {
            referenceType: 'entity',
            docId: rawValue,
            cacheLabel: ref?.entityRecordName,
          };
          if ('entityGroupId' in targetColumn && targetColumn.entityGroupId) {
            entityRef.entityGroupId = targetColumn.entityGroupId;
          }
          return entityRef;
        }

        return rawValue as ArCellValue;
      })();

      initialCells[targetTypeId] = { value };
    }

    writeQuantityCell(initialCells, options);

    if (split && !protectedTypeIds.has(split.typeId)) {
      initialCells[split.typeId] = { value: split.filterValue };
    }

    const rowId = await actions.addRow(
      initialCells,
      options?.afterRowId ? { afterRowId: options.afterRowId } : undefined,
    );
    if (!rowId) {
      messageApi.error('Failed to insert Line Item row.');
      return null;
    }
    return rowId;
  }, [actions, columns, disabled, get, lineItemsColumn, messageApi, protectedTypeIds, split, writeQuantityCell]);

  const insertManualRow = useCallback(async (
    recordName: string,
    fields: Fields,
    entityReferences: EntityRecord['entityReferences'],
    options?: ArSignifyInsertOptions,
  ): Promise<string | null> => {
    if (disabled || !actions || !lineItemsColumn) return null;

    if (protectedTypeIds.has(lineItemsColumn.typeId)) {
      messageApi.error('Unable to insert: Line Items column is protected.');
      return null;
    }

    const initialCells: Record<TypeId, ArCell> = {};

    const manualEntry: ArManualEntryValue = {
      valueType: 'manual',
      value: recordName,
      ...(Object.keys(fields).length > 0 && { fields }),
      ...(entityReferences && Object.keys(entityReferences).length > 0 && { entityReferences }),
    };
    initialCells[lineItemsColumn.typeId] = { value: manualEntry };

    for (const mapping of lineItemsColumn.elementMapping ?? []) {
      if (mapping.target.type !== 'column') continue;

      const targetTypeId = mapping.target.id as TypeId;
      if (!targetTypeId) continue;
      if (targetTypeId === lineItemsColumn.typeId) continue;
      if (protectedTypeIds.has(targetTypeId)) continue;

      const targetColumn = columns.find((c) => c.typeId === targetTypeId);
      if (!targetColumn) continue;

      const rawValue = (() => {
        if (mapping.source.type === 'field') {
          return fields[mapping.source.id]?.value ?? null;
        }
        const ref = entityReferences?.[mapping.source.id];
        return ref?.entityRecordId ?? null;
      })();

      if (rawValue === null || rawValue === undefined) continue;

      const value: ArCellValue = (() => {
        if (
          targetColumn.uiElementType === UIElementType.Reference &&
          'referenceType' in targetColumn &&
          targetColumn.referenceType === 'entity' &&
          typeof rawValue === 'string' &&
          rawValue.length > 0
        ) {
          const ref = entityReferences?.[mapping.source.id];
          const entityRef: ArEntityReference = {
            referenceType: 'entity',
            docId: rawValue,
            cacheLabel: ref?.entityRecordName,
          };
          if ('entityGroupId' in targetColumn && targetColumn.entityGroupId) {
            entityRef.entityGroupId = targetColumn.entityGroupId;
          }
          return entityRef;
        }
        return rawValue as ArCellValue;
      })();

      initialCells[targetTypeId] = { value };
    }

    writeQuantityCell(initialCells, options);

    if (split && !protectedTypeIds.has(split.typeId)) {
      initialCells[split.typeId] = { value: split.filterValue };
    }

    const rowId = await actions.addRow(
      initialCells,
      options?.afterRowId ? { afterRowId: options.afterRowId } : undefined,
    );
    if (!rowId) {
      messageApi.error('Failed to insert row.');
      return null;
    }
    return rowId;
  }, [actions, columns, disabled, lineItemsColumn, messageApi, protectedTypeIds, split, writeQuantityCell]);

  const findRowsHoldingLineItems = useCallback(async (
    identities: SignifyFetchJobRowIdentities,
  ): Promise<SignifyFetchJobExistingRows> => {
    const empty: SignifyFetchJobExistingRows = { productIds: new Set(), recordIds: new Set() };
    if (!lineItemsColumn || rowsPersist.persistType !== 'collection') return empty;

    const productIdPath = `cells.${lineItemsColumn.typeId}.value.fields.manufacturersProductId.value`;
    const recordIdPath = `cells.${lineItemsColumn.typeId}.value.docId`;
    const splitConstraints = split && !protectedTypeIds.has(split.typeId)
      ? [where(`cells.${split.typeId}.value`, '==', split.filterValue)]
      : [];
    const chunksOf = (ids: readonly string[]): string[][] => {
      const unique = Array.from(new Set(ids));
      return Array.from({ length: Math.ceil(unique.length / IN_FILTER_LIMIT) }, (_unused, index) =>
        unique.slice(index * IN_FILTER_LIMIT, (index + 1) * IN_FILTER_LIMIT));
    };

    const [productSnapshots, recordSnapshots] = await Promise.all([
      Promise.all(chunksOf(identities.productIds).map((chunk) =>
        getDocs(query(rowsPersist.collectionRef, where(productIdPath, 'in', chunk), ...splitConstraints)))),
      Promise.all(chunksOf(identities.recordIds).map((chunk) =>
        getDocs(query(rowsPersist.collectionRef, where(recordIdPath, 'in', chunk), ...splitConstraints)))),
    ]);

    const found: SignifyFetchJobExistingRows = { productIds: new Set(), recordIds: new Set() };
    productSnapshots.forEach((snapshot) => snapshot.docs.forEach((document) => {
      const value = document.get(productIdPath);
      if (typeof value === 'string') found.productIds.add(value);
    }));
    recordSnapshots.forEach((snapshot) => snapshot.docs.forEach((document) => {
      const value = document.get(recordIdPath);
      if (typeof value === 'string') found.recordIds.add(value);
    }));
    return found;
  }, [lineItemsColumn, protectedTypeIds, rowsPersist, split]);

  return { insertRowFromLineItem, insertManualRow, findRowsHoldingLineItems };
}

export { useArSignifyInsert };
export type { ArSignifyInsertOptions, LineItemsColumn };
